import * as React from 'react';
import { Button } from '../../../components/ui/button';
import { useConfirmationDialog } from '../../../components/ui/use-confirmation-dialog';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { useScenarioSaveAs } from '../hooks/useScenarioSaveAs';
import { useServerDraftActions } from '../hooks/useServerDraftActions';
import { useCommitScenarioAction } from '../hooks/useCommitScenarioAction';
import type { OrgArtifactViewDefinitionLike } from '../models/orgArtifactKind';
import { useSuccessionDraftSummary } from '../hooks/useSuccessionDraftSummary';
import { enqueueSuccessionTransition } from '../hooks/orgPlanningCommandSequencer';
import { successionOptimisticContext } from '../stores/successionOptimisticSlateStore';
import { useQueryClient } from '@tanstack/react-query';
import { openAtlasChanges } from '../stores/atlasChangesStore';
import type { OrganizationChangeApplyState } from '../models/OrganizationChangeApplyResult';
import { MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../../components/ui/dropdown-menu';
import { presentOperationalDiff } from '../services/operationalChangeDiffPresentation';
import { useAtlasDraftSaveStatus, useClaimAtlasDraftSaveStatus } from '../hooks/useAtlasDraftSaveStatus';
import { useAtlasOperationalRecovery, type AtlasOperationalRecoveryReceipt } from '../hooks/useAtlasOperationalRecovery';

export function operationalStatusLabel(
  state: OrganizationChangeApplyState,
  destinationLabel?: string,
): string {
  if (state === 'applied') return 'Applied in Atlas';
  if (state === 'reconciled') return 'Reconciled with source';
  if (state === 'failed') return 'Submission failed';
  if (state === 'conflict') return 'Source changed — review again';
  if (state === 'writing') return 'Writing to source…';
  if (state === 'queued') return 'Submitting…';
  return `Awaiting ${destinationLabel ?? 'source'} sync`;
}

const SUBMITTED_OPERATIONAL_STATES: Partial<Record<AtlasOperationalRecoveryReceipt['state'], OrganizationChangeApplyState>> = {
  submitted: 'queued',
  in_progress: 'writing',
  awaiting_sync: 'awaiting_reconciliation',
};

/** The chip narrates a submitted change instead of offering a review it cannot act on. */
export function submittedOperationalChipLabel(receipt: AtlasOperationalRecoveryReceipt | null | undefined): string | null {
  const state = receipt ? SUBMITTED_OPERATIONAL_STATES[receipt.state] : undefined;
  return state ? operationalStatusLabel(state, receipt?.destinationLabel) : null;
}

/**
 * Read-only draft status and launchers for the shared Changes host.
 * Apply/Discard remain owned by that host. Planning-diagram handoff stays
 * visible for a client draft, but cannot race a pending or unresolved save.
 */
export const AtlasDraftChipActions: React.FC<{
  folderId: string | null;
  diagramId: string | null | undefined;
  canWrite: boolean;
  /** Live-chart drafts can be moved into a linked planning diagram before the server head exists. */
  showPlanningDiagramAction?: boolean;
  hasClientDraft?: boolean;
  /** Changes in the client canvas draft; Apply is offered once there is at least one. */
  clientChangeCount?: number;
  viewDefinition?: OrgArtifactViewDefinitionLike | null;
  isPlanningChart?: boolean;
}> = ({
  folderId,
  diagramId,
  canWrite,
  showPlanningDiagramAction = false,
  hasClientDraft = false,
  clientChangeCount = 0,
  viewDefinition = null,
  isPlanningChart = false,
}) => {
  const queryClient = useQueryClient();
  const { headChangeSetId, mutationState } = useSuccessionDraftSummary({ folderId, diagramId });
  const serverDraft = useServerDraftActions({ folderId, diagramId, headChangeSetId });
  const { saveAs, isSaving } = useScenarioSaveAs();
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();
  const { commitWithConfirm, isCommitting } = useCommitScenarioAction({ folderId, diagramId, viewDefinition });

  const handleSaveAs = React.useCallback(async () => {
    const confirmed = await requestConfirmation({
      title: isPlanningChart ? 'Duplicate this planning chart?' : 'Save this draft as its own chart?',
      description: isPlanningChart
        ? 'Creates a separate linked planning chart with a copy of this draft. This planning chart and its draft remain unchanged.'
        : 'Opens a new linked planning chart carrying these changes as its draft — it can still be applied to the org from there. This chart keeps its draft too, until you apply or discard it.',
      confirmLabel: isPlanningChart ? 'Duplicate chart' : 'Save as chart',
      variant: 'default',
    });
    if (!confirmed) return;
    // The fork machinery keys on the active canvas scenario for its name and
    // metadata. A server-only draft has none, so give it the same shell the
    // succession gate would have created.
    const scenarios = useScenariosStore.getState();
    if (!scenarios.activeScenarioId) {
      const scenario = scenarios.createScenario('Draft changes');
      useScenariosStore.getState().setActiveScenario(scenario.id);
    }
    if (folderId && diagramId) {
      await enqueueSuccessionTransition(
        successionOptimisticContext(folderId, diagramId),
        (signal) => saveAs('plan', undefined, signal),
        queryClient,
        { lifecycleKey: 'save-as' },
      );
      return;
    }
    await saveAs('plan');
  }, [diagramId, folderId, isPlanningChart, queryClient, requestConfirmation, saveAs]);

  const canvasSave = useAtlasDraftSaveStatus(folderId ?? '', diagramId ?? '').state;
  const activeOperation = useAtlasOperationalRecovery(folderId ?? '', diagramId ?? '');
  const submittedLabel = submittedOperationalChipLabel(activeOperation.data);
  const head = serverDraft.headChangeSetId;
  // Canvas edits save through their own port; a review opened mid-save would
  // pin the head that save is about to supersede.
  const saving = mutationState.phase === 'saving' || canvasSave === 'saving' || canvasSave === 'refreshing';
  const unsaved = mutationState.phase === 'unsaved' || canvasSave === 'failed' || canvasSave === 'unknown';
  const unsavedReason = mutationState.phase === 'unsaved' ? mutationState.error
    : unsaved ? 'Resolve the unsaved edit before reviewing changes.' : undefined;
  useClaimAtlasDraftSaveStatus(folderId, diagramId, Boolean(head || saving || unsaved));
  const openPlanningDiagramAction = showPlanningDiagramAction && hasClientDraft ? (
    <Button type="button" variant="ghost" size="sm"
      data-testid="tid.orgchart.modeChip.openPlanningDiagram"
      disabled={!canWrite || isSaving || saving || unsaved}
      onClick={() => { void handleSaveAs(); }}
      title="Open this draft in a linked planning diagram"
      className="h-7 max-w-full rounded-full px-2.5 text-center text-xs whitespace-normal"
    >{isSaving ? "Opening…" : "Open planning diagram"}</Button>
  ) : null;
  // A client canvas draft has no server head yet. Apply here is the same
  // shared action as the assistant banner: it prepares the change and opens
  // its review, so the chip never narrates a draft it cannot act on.
  const applyClientDraftAction = hasClientDraft && clientChangeCount > 0 ? (
    <Button type="button" size="sm"
      data-testid="tid.orgchart.modeChip.applyDraft"
      disabled={!canWrite || isCommitting || isSaving || saving || unsaved}
      onClick={() => { void commitWithConfirm(); }}
      className="h-7 max-w-full rounded-full bg-amber-600 px-3 text-center text-xs text-white whitespace-normal hover:bg-amber-700"
    >{isCommitting ? 'Applying…' : 'Apply'}</Button>
  ) : null;
  if (!head && !saving && !unsaved) {
    return applyClientDraftAction || openPlanningDiagramAction ? (
      <>
        <div className="flex items-center justify-center gap-1.5">
          {applyClientDraftAction}
          {openPlanningDiagramAction}
        </div>
        {confirmationDialog}
      </>
    ) : null;
  }

  return (
    <>
      <div className="flex items-center justify-center gap-1.5">
        <Button
          type="button"
          size="sm"
          data-testid="tid.orgchart.modeChip.reviewDraft"
          disabled={!folderId || !diagramId || !head || saving || unsaved || Boolean(submittedLabel)}
          title={unsavedReason}
          onClick={() => { if (folderId && diagramId && head) openAtlasChanges(folderId, diagramId, head); }}
          className="h-7 max-w-full rounded-full bg-amber-600 px-3 text-center text-xs text-white whitespace-normal hover:bg-amber-700"
        >
          {canvasSave === 'unknown' ? 'Save unconfirmed' : unsaved ? 'Save failed' : saving ? 'Saving draft…'
            : submittedLabel ? submittedLabel : serverDraft.review
            ? `Review changes (${presentOperationalDiff(serverDraft.review.diff).length})`
            : 'Review changes'}
        </Button>
        {!serverDraft.isOperational && head && isPlanningChart ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" aria-label="Planning chart options"
                className="h-7 w-7 shrink-0 rounded-full"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!canWrite || saving || unsaved || isSaving || Boolean(submittedLabel)}
                onSelect={() => { void handleSaveAs(); }}>Duplicate planning chart</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : !serverDraft.isOperational && head ? (
          <Button type="button" variant="ghost" size="sm"
            disabled={!canWrite || saving || unsaved || isSaving || Boolean(submittedLabel)}
            onClick={() => void handleSaveAs()} className="h-7 max-w-full rounded-full px-2.5 text-center text-xs whitespace-normal"
          >{isSaving ? 'Saving…' : 'Save as planning chart'}</Button>
        ) : null}
        {!head ? openPlanningDiagramAction : null}
      </div>
      {confirmationDialog}
    </>
  );
};
